import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { splitBaseSceneNote } from './base-scene-note';

import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';
import { computeSceneGroupShortIds, resolveSceneGroupHandle } from './scene-group-addressing';
import { resolveSplitBoundaryOperation } from './split-placement';
import { getInterleavedTranscript } from './transcript-tokens';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary, splitSceneGroupOperation } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const splitSceneGroupSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the voiceover or talking-head scene group to split (from the video state).',
        ),
    words: z
        .array(z.string())
        .describe(
            'Consecutive tokens from the transcript; the scene group is split immediately after the last of these. Provide enough tokens to uniquely identify the location — they must match exactly, in order, as spelled in the transcript, including any `<pause seconds="N"/>` token that falls between them.',
        ),
});
export type SplitSceneGroupArgs = z.infer<typeof splitSceneGroupSchema>;

export class SplitSceneGroupTool extends VideoStateTool<typeof splitSceneGroupSchema> {
    protected override readonly schema = splitSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.splitSceneGroup;
    protected override readonly toolDescription = `Split a voiceover or talking-head scene group into two at a point in its transcript. The split happens immediately AFTER the given sequence of transcript words: those words (and everything before them) stay in the first scene group, and everything after them moves into a new scene group inserted right after. The words must appear consecutively in the transcript and cannot be the very last word (there must be at least one word after the split point). Choose ${TOOL_NAMES.splitSceneGroup} when the user wants a structural break in the narration, you need a silent scene or non-speaking segment between spoken parts, or parts of the transcript need to become independently editable sections. (To emphasize specific words without changing the audio structure instead, use ${TOOL_NAMES.addVoiceoverScene}.)`;

    public override getToolDisplayName(): string {
        return 'Splitting scene group';
    }

    public get statusIcon(): string {
        return 'call_split';
    }

    public async execute(args: SplitSceneGroupArgs, context: ToolExecutionContext) {
        const { words: requestedWords } = args;
        const { parentActionGroup } = context;

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);

        // Only voicedriven scene groups (voiceover / talking head) have a transcript to split on.
        // originalTranscript is eagerly kept in sync with the lexical editor (LuminarySyncPlugin),
        // so its word times reflect any edits the user has made.
        const transcript = sceneGroup.originalTranscript;
        const transcriptTokens = getInterleavedTranscript(this.getState(), sceneGroup);
        if (!transcript || transcriptTokens.length === 0) {
            return `Error: Scene group ${sceneGroup.id} does not have a transcript. Only voiceover and talking-head scene groups can be split.`;
        }

        const { resolveSplitBoundary, splitSceneGroup } = makeOperationsCallable(
            { resolveSplitBoundaryOperation, splitSceneGroupOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        const boundary = resolveSplitBoundary(sceneGroup, requestedWords);
        if ('error' in boundary) {
            return `Error: ${boundary.error}`;
        }
        const { splitMillis } = boundary;

        try {
            const groupIdsBeforeSplit = new Set(this.luminary.sceneGroups.map((group) => group.id));

            splitSceneGroup(sceneGroup.id, splitMillis, false, parentActionGroup);

            const groupShortIds = computeSceneGroupShortIds(
                this.luminary.sceneGroups.map((group) => group.id),
            );
            const originalShortId = groupShortIds.get(sceneGroup.id) ?? sceneGroup.id;
            const followingGroup = this.luminary.sceneGroups[sceneGroup.index + 1];

            // A real split inserts a fresh group right after the target. If none appeared there the
            // split was a no-op (the point is at/after this group's end — usually its words already
            // moved to the following group), so error out and let the model re-target.
            if (!followingGroup || groupIdsBeforeSplit.has(followingGroup.id)) {
                const retargetHint = followingGroup
                    ? ` If you already split this group, the words after "${requestedWords.join(' ')}" now belong to the following scene group (${groupShortIds.get(followingGroup.id) ?? followingGroup.id}) — split that group instead.`
                    : '';
                return `Error: Splitting scene group ${originalShortId} after "${requestedWords.join(' ')}" had no effect — there is nothing after that point in this scene group to move into a new group.${retargetHint}`;
            }

            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            const newShortId = groupShortIds.get(followingGroup.id) ?? followingGroup.id;

            // The split duplicates the group and cuts each half, so the new group carries copies of
            // whatever the original held — base scenes only if some stretch was uncovered.
            const state = this.getState();
            const hasBaseScene = followingGroup.scenes.some(
                (scene) => !isSceneAnOverlay(state, scene),
            );

            return `Success: Split scene group ${originalShortId} after "${requestedWords.join(' ')}". It is now two scene groups: ${originalShortId} keeps everything up to and including those words, and a new scene group ${newShortId} directly after it holds the rest.${hasBaseScene ? splitBaseSceneNote() : ''}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error splitting scene group: ${errorMessage}`;
        }
    }
}
